// Import useVirtualizer hook from @tanstack/react-virtual
import '@gpstrackit/react-controls/src/Table/table.scss';
import { useVirtualizer } from '@tanstack/react-virtual';
import cx from 'classnames';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import _ from 'lodash';
import React, { forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { Checkbox, CircularProgress, Grid, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';

import { LinearProgress, MenuComponent } from '@gpstrackit/react-controls';
import Pagination from './.components/Pagination';
import TableHeader from './.components/header.js';
// Empty state component for no-data scenarios
import TableEmptyState from './.components/TableEmptyState';
// Skeleton rows for infinite scroll load-more visual feedback
import SkeletonTableRow from './.components/SkeletonTableRow';
// Error indicator for infinite scroll load-more failures
import ErrorIndicator from './.components/ErrorIndicator';
// Selection limit notification handled by onSelectionLimitReached callback prop

// ════════════════════════════════════════════════════════════════════════════
// CONSTANTS - Magic numbers extracted for maintainability and configurability
// These serve as DEFAULT values when user does not provide virtualizationConfig
// ════════════════════════════════════════════════════════════════════════════

/** @constant {number} Number of skeleton rows to show during infinite scroll load-more */
const SKELETON_PLACEHOLDER_COUNT = 5;

/** @constant {number} Row height adjustment pixels per density level */
const DENSITY_CONDENSATION_HEIGHT = 7;

/** @constant {number} Default estimated row height (pixels) when no density is set */
const DEFAULT_ROW_HEIGHT = 53;

/** @constant {number} Base row height for density calculations */
const DENSITY_BASE_ROW_HEIGHT = 21;

/** @constant {number} Multiplier applied to density row height calculation */
const DENSITY_HEIGHT_MULTIPLIER = 2;

/** @constant {number} Virtualizer overscan - buffer rows above/below viewport for smooth scrolling */
const VIRTUALIZER_OVERSCAN = 15;

/** @constant {number} Threshold rows from end to trigger load-more */
const SCROLL_NEAR_END_THRESHOLD = 5;

/** @constant {number} Default rows per page in infinite scroll mode */
const DEFAULT_ROWS_INFINITE_SCROLL = 25;

/**
 * Resolves the unique key for a row item.
 * Supports both string field names and custom key generator functions.
 * 
 * @param {string|Function} keyColumn - Field name string OR function(item, index) => key
 * @param {Object} item - The row data object
 * @param {number} index - The row index in the data array
 * @returns {string|number} The resolved unique key for the row
 * 
 * @example
 * // String field name (default behavior)
 * resolveRowKey('id', { id: 123 }, 0) // => 123
 * 
 * @example
 * // Custom function for composite keys
 * resolveRowKey((item, idx) => `${item.id}-${idx}`, { id: 123 }, 5) // => "123-5"
 */
const resolveRowKey = (keyColumn, item, index) => {
    if (typeof keyColumn === 'function') {
        return keyColumn(item, index);
    }
    return item?.[keyColumn];
};

/** @constant {number} Default rows per page in pagination mode */
const DEFAULT_ROWS_PAGINATION = 10;

/** @constant {number} Default maximum selectable items for bulk actions */
const DEFAULT_SELECTION_MAX_ITEMS = 500;

/** @constant {number} Base left offset (pixels) for locked columns (checkbox width) */
const LOCKED_COLUMN_BASE_OFFSET = 43;

/** @constant {number} PDF export font size in points */
const PDF_FONT_SIZE = 12;

/** @constant {number} PDF table start Y position in points */
const PDF_START_Y = 40;

/** @constant {number} PDF minimum cell width in points */
const PDF_MIN_CELL_WIDTH = 50;

/** @constant {number} Maximum density level (cycles 1 -> 2 -> 3 -> 1) */
const DENSITY_MAX = 3;

/** @constant {number} Loading indicator vertical padding in pixels */
const LOADING_INDICATOR_PADDING = 16;

/** @constant {number} Loading spinner size in pixels */
const LOADING_SPINNER_SIZE = 24;

/** @constant {number} Pagination container top padding in pixels */
const PAGINATION_PADDING_TOP = 5;

// Memoized row component — prevents re-render of unchanged rows during scroll
const VirtualTableRow = React.memo(React.forwardRef(({
    row, dataIndex, visibleColumns,
    disableItemSelection, lockedColumns, lockedColumnsHandler,
    menuOptions, onClickMenu, customColumn,
    isSelected, onToggleSelection,
    density, defaultDensity
}, ref) => (
    <TableRow
        data-index={dataIndex}
        ref={ref}
        style={{ backgroundColor: dataIndex % 2 === 0 ? '#f9f9f9' : '#fff' }}
        aria-selected={isSelected}
        tabIndex={0}
        role="row"
    >
        {
            !disableItemSelection &&
            <TableCell padding="checkbox" className={cx(lockedColumns && "locked-columns")}>
                <Checkbox
                    style={{ color: "black", zIndex: 0 }}
                    checked={isSelected}
                    onClick={() => onToggleSelection(row)}
                    inputProps={{ 'aria-label': `Select row ${dataIndex + 1}` }}
                    size="small"
                />
            </TableCell>
        }
        {
            menuOptions &&
            <TableCell
                padding="checkbox"
                className={cx("context-menu-container context-menu-container-override", lockedColumns && "locked-columns")}
                ref={(el) => {
                    if (el && lockedColumns) {
                        const leftValue = disableItemSelection ? 0 : 43;
                        el.style.setProperty('left', `${leftValue}px`, 'important');
                    }
                }}
            >
                <div style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                    <MenuComponent
                        options={menuOptions(row)}
                        icon="more_vert"
                        className="context-menu-table"
                        onClick={(type, value, clickEvent) => {
                            onClickMenu && onClickMenu(type, value, clickEvent);
                        }}
                        value={row}
                    />
                </div>
            </TableCell>
        }
        {
            customColumn &&
            <TableCell 
                padding="checkbox"
                className={cx("context-menu-container context-menu-container-override", lockedColumns && "locked-columns")}
                ref={(el) => {
                    if (el && lockedColumns) {
                        const leftValue = (disableItemSelection ? 0 : 43) + (menuOptions ? 43 : 0);
                        el.style.setProperty('left', `${leftValue}px`, 'important');
                    }
                }}
            >
                <div style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                    {customColumn?.render ? customColumn.render(row) : <></>}
                </div>
            </TableCell>
        }
        {
            visibleColumns.map((col, index) => {
                return (
                    <TableCell key={col.key}
                        className={cx(lockedColumns && col?.locked && "locked-columns")}
                        style={{
                            left: lockedColumns && col?.locked && lockedColumnsHandler(index, visibleColumns, disableItemSelection, "cell"),
                            // Use DENSITY_CONDENSATION_HEIGHT constant
                            paddingTop: defaultDensity && ((density - 1) * DENSITY_CONDENSATION_HEIGHT),
                            paddingBottom: defaultDensity && ((density - 1) * DENSITY_CONDENSATION_HEIGHT)
                        }}
                        id={`cell-${col.key}`}
                    >
                        {
                            col.render
                                ? col.render(row)
                                : row[col.key]?.toString()
                        }
                    </TableCell>
                )
            })
        }
    </TableRow>
)));

const TableView = forwardRef((props, ref) => {

    // ════════════════════════════════════════════════════════════════════════════
    // GROUPED PROPS RESOLUTION
    // Supports both new grouped config objects AND legacy flat props
    // ════════════════════════════════════════════════════════════════════════════

    // Destructure grouped config objects from props
    const {
        dataConfig,      // { data, loading, isLoadingMore, nextToken, total, moreRowsAvailable }
        tableConfig,     // { columns, keyColumn, lockedColumns, customColumn, menuOptions, onChangeColumns }
        uiConfig,        // { topHeight, height, fixHeader, hidePagination, hideAllInPagination, defaultDensity }
        selectionConfig, // { disabled, maxItems, onSelectedItems, onSelectionLimitReached }
        pagingConfig,    // { infiniteScroll, valuesPerPage, onChangePage, onChangeRowsPerPage }
        exportConfig,    // { name, orientation }
        messagesConfig,  // { messages, noMoreItemsMessage, emptyConfig }
        // New virtualization config for customizing table performance settings
        virtualizationConfig, // { skeletonCount, rowHeight, overscan, scrollThreshold, ... }
        // Callback props (kept flat for simplicity)
        onChangeSort,
        onClickMenu,
        onChangeDisplayData,
        onChangeDensity,
        // Grouping props (rarely used, kept flat)
        grouping,
        groupingValueToCompare,
        groupingArea,
        searchText,
    } = props;

    // ════════════════════════════════════════════════════════════════════════════
    // PROPS DESTRUCTURING WITH DEFAULTS
    // ════════════════════════════════════════════════════════════════════════════

    /**
     * @typedef {Object} DataConfig
     * @property {Array} [data] - Data items
     * @property {boolean} [loading] - Loading state
     * @property {boolean} [isLoadingMore] - Infinite scroll loading state
     * @property {string|null} [nextToken] - Pagination cursor
     * @property {number} [total] - Total count
     * @property {boolean} [moreRowsAvailable] - More rows available
     * @property {string|null} [error] - Error message
     */
    const {
        data = [],
        loading = false,
        isLoadingMore = false,
        nextToken = null,
        total: totalFromData = 0,
        moreRowsAvailable,
        error = null,
    } = dataConfig ?? {};

    /**
     * @typedef {Object} TableConfig
     * @property {Array} [columns] - Column definitions
     * @property {string|Function} [keyColumn] - Unique row identifier: field name string OR function(item, index) => key
     * @property {boolean} [lockedColumns] - Enable sticky columns
     * @property {Object} [customColumn] - Custom column renderer
     * @property {Function} [menuOptions] - Row context menu function
     * @property {Function} [onChangeColumns] - Column change callback
     */
    const {
        columns = [],
        keyColumn = 'id',  // Default to 'id' for row identification
        lockedColumns = false,
        customColumn,
        menuOptions,
        onChangeColumns,
    } = tableConfig ?? {};

    /**
     * @typedef {Object} UIConfig
     * @property {number} [topHeight] - Top offset
     * @property {string|number} [height] - Table height
     * @property {boolean} [fixHeader] - Sticky header
     * @property {boolean} [hidePagination] - Hide pagination
     * @property {boolean} [hideAllInPagination] - Hide "All" option
     * @property {number} [defaultDensity] - Initial density
     */
    const {
        topHeight: uiTopHeight,
        height: uiHeight,
        fixHeader: uiFixHeader = true,
        hidePagination: uiHidePagination = false,
        hideAllInPagination: uiHideAllInPagination = false,
        defaultDensity: uiDefaultDensity,
    } = uiConfig ?? {};

    /**
     * @typedef {Object} SelectionConfig
     * @property {boolean} [disabled] - Disable selection
     * @property {number} [maxItems] - Max selectable items
     * @property {Function} [onSelectedItems] - Selection change callback
     * @property {boolean} [showCounter] - Show selection counter
     * @property {Function} [onSelectionLimitReached] - Limit reached callback
     */
    const {
        disabled: selectionDisabled = false,
        maxItems: selectionMaxItems = DEFAULT_SELECTION_MAX_ITEMS,
        showCounter: selectionShowCounter = true,
        onSelectedItems: selectionOnSelectedItems,
        onSelectionLimitReached: selectionOnSelectionLimitReached,
    } = selectionConfig ?? {};

    /**
     * @typedef {Object} PagingConfig
     * @property {boolean} [infiniteScroll] - Enable infinite scroll
     * @property {Array} [valuesPerPage] - Page size options
     * @property {Function} [onChangePage] - Page change callback
     * @property {Function} [onChangeRowsPerPage] - Rows change callback
     * @property {Function} [onRetry] - Retry callback
     * @property {number} [defaultPage] - Default page number
     * @property {Object} [defaultSort] - Default sort configuration
     * @property {number} [defaultRowsPerPage] - Default rows per page
     */
    const {
        infiniteScroll: pagingInfiniteScroll = false,
        valuesPerPage: pagingValuesPerPage,
        onChangePage: pagingOnChangePage,
        onChangeRowsPerPage: pagingOnChangeRowsPerPage,
        onRetry: pagingOnRetry,
        defaultPage: pagingDefaultPage = 1,
        currentPage: pagingCurrentPage,
        defaultSort: pagingDefaultSort,
        defaultRowsPerPage: pagingDefaultRowsPerPage,
    } = pagingConfig ?? {};

    /**
     * @typedef {Object} ExportConfig
     * @property {string} [name] - Export filename
     * @property {string} [orientation] - PDF orientation
     */
    const {
        name: exportName,
        orientation: exportOrientation,
    } = exportConfig ?? {};

    /**
     * @typedef {Object} MessagesConfig
     * @property {Object} [messages] - i18n messages
     * @property {string} [noMoreItemsMessage] - End of list message
     * @property {Object} [emptyConfig] - Empty state config
     * @property {string} [emptyType] - Empty state type
     * @property {string} [errorLoadingMore] - Error message
     * @property {string} [retry] - Retry label
     */
    const {
        messages = {},
        noMoreItemsMessage: messagesNoMoreItemsMessage,
        emptyConfig: messagesEmptyConfig,
        emptyType: messagesEmptyType,
        errorLoadingMore: messagesErrorLoadingMore,
        retry: messagesRetry,
    } = messagesConfig ?? {};

    /**
     * @typedef {Object} VirtualizationConfig
     * @property {number} [skeletonCount] - Number of skeleton rows during load-more (default: 5)
     * @property {number} [rowHeight] - Default estimated row height in pixels (default: 53)
     * @property {number} [baseRowHeight] - Base row height for density calculations (default: 21)
     * @property {number} [condensationHeight] - Row height adjustment per density level (default: 7)
     * @property {number} [heightMultiplier] - Multiplier for density height calculation (default: 2)
     * @property {number} [overscan] - Buffer rows above/below viewport for smooth scrolling (default: 15)
     * @property {number} [scrollThreshold] - Rows from end to trigger load-more (default: 5)
     * @property {number} [lockedColumnOffset] - Base pixel offset for locked columns (default: 43)
     */
    const {
        skeletonCount: virtSkeletonCount = SKELETON_PLACEHOLDER_COUNT,
        rowHeight: virtRowHeight = DEFAULT_ROW_HEIGHT,
        baseRowHeight: virtBaseRowHeight = DENSITY_BASE_ROW_HEIGHT,
        condensationHeight: virtCondensationHeight = DENSITY_CONDENSATION_HEIGHT,
        heightMultiplier: virtHeightMultiplier = DENSITY_HEIGHT_MULTIPLIER,
        overscan: virtOverscan = VIRTUALIZER_OVERSCAN,
        scrollThreshold: virtScrollThreshold = SCROLL_NEAR_END_THRESHOLD,
        lockedColumnOffset: virtLockedColumnOffset = LOCKED_COLUMN_BASE_OFFSET,
    } = virtualizationConfig ?? {};

    const [page, setPage] = useState(pagingDefaultPage);
    // Use paging.infiniteScroll for initial state
    // Use constants for default rows per page
    const [rowsPerPage, setRowsPerPage] = useState(pagingInfiniteScroll ? DEFAULT_ROWS_INFINITE_SCROLL : DEFAULT_ROWS_PAGINATION);
    const [sort, setSort] = useState({ key: 'nametag', order: 'desc' });
    const [selectedItems, setSelectedItems] = useState([]);
    const [density, setDensity] = useState(0);
    // Batch sequential: track processed item IDs so "Select All" skips them
    const [processedItemIds, setProcessedItemIds] = useState(new Set());

    // Create ref for the scroll container (TableContainer)
    const tableContainerRef = useRef(null);
    // Cache column widths to avoid DOM reads in render path
    const columnWidthCache = useRef({});
    // Ref for scroll-lock cleanup function during column drag
    const scrollLockRef = useRef(null);
    // Ref for stable selection toggle callback
    const onSelectedItemsRef = useRef(selectionOnSelectedItems);
    onSelectedItemsRef.current = selectionOnSelectedItems;
    // Stable ref for column change callback
    const onChangeColumnsRef = useRef(onChangeColumns);
    onChangeColumnsRef.current = onChangeColumns;
    // Track whether user has scrolled - prevents premature scroll-fetch on mount
    const hasUserScrolledRef = useRef(false);
    // Track if sort was set imperatively by parent - prevents useEffect override (Fix C)
    const sortSetImperativelyRef = useRef(false);

    // Use resolved configs instead of direct props access
    useEffect(() => {
        if (data && data.length > 0 && page == 0) {
            setPage(pagingDefaultPage || 1)
            setDensity(uiDefaultDensity || 0);
            // Only apply pagingDefaultSort if sort was NOT already set imperatively by parent
            // This prevents race condition where parent's applySortPreferences is overwritten
            if (!sortSetImperativelyRef.current && pagingDefaultSort) {
                setSort(pagingDefaultSort);
            }
            pagingDefaultRowsPerPage && setRowsPerPage(pagingDefaultRowsPerPage);
            if (onChangeDisplayData) onChangeDisplayData(filterData)
        }
    }, [data])

    // Sync displayed page with parent (e.g. resets to 1 when filters change)
    useEffect(() => {
        if (pagingCurrentPage !== undefined) setPage(pagingCurrentPage);
    }, [pagingCurrentPage])

    let currentGroup
    let sortGroup
    if (grouping)
        sortGroup = { key: groupingValueToCompare, order: 'asc' }

    useImperativeHandle(ref, () => ({
        selectedItems: selectedItems,
        exportPDF: exportPDF,
        exportCSV: exportCSV,
        setPage: setPage,
        setSelectedItems: setSelectedItems,
        onChangeDensity: handleDensityChange,
        density: density,
        setRowsPerPage: setRowsPerPage,
        setSort: (newSort) => {
            // Mark that sort was set imperatively by parent - Fix C
            sortSetImperativelyRef.current = true;
            setSort(newSort);
        },
        // Batch sequential: mark items as processed so next "Select All" skips them
        markAsProcessed: (items) => {
            if (!items || items.length === 0) return;
            setProcessedItemIds(prev => {
                const next = new Set(prev);
                items.forEach(item => {
                    const key = typeof keyColumn === 'function' ? keyColumn(item, 0) : item?.[keyColumn];
                    if (key != null) next.add(key);
                });
                return next;
            });
            // Clear selection after marking as processed
            setSelectedItems([]);
            onSelectedItemsRef.current && onSelectedItemsRef.current([]);
        },
        // Reset processed tracking (e.g., on filter/search change)
        resetProcessed: () => {
            setProcessedItemIds(new Set());
        },
        processedCount: processedItemIds.size,
    }))

    // Use resolved configs instead of direct props access
    const [filterData, total] = useMemo(() => {
        // Infinite scroll: nextToken is now always defined (string or null, never undefined)
        // No total needed — pagination is driven by nextToken

        if (pagingInfiniteScroll) {
            return [data, 0];
        }

        if (pagingOnChangePage) {
            return [data, totalFromData];
        }

        let sorts = [];
        let viewData = data;
        let localTotal = data.length;


        if (sortGroup) sorts.push(sortGroup)
        if (sort) sorts.push(sort)

        if (searchText && typeof searchText !== 'undefined' && searchText.length > 0) {
            viewData = data.filter(x => {
                let result = false;
                columns.forEach(col => {
                    if (getClearValue(x[col.key]).toString().toLowerCase().indexOf(searchText.toLowerCase()) >= 0)
                        result = true;
                });

                return result
            })

            localTotal = viewData.length
        }

        if (sorts && sorts.length > 0)
            viewData = multipleSort(viewData || [], sorts || [])


        return [viewData, localTotal];
    }, [data, pagingInfiniteScroll, totalFromData, pagingOnChangePage, sortGroup, sort, searchText, columns]);

    // Use resolved configs for filterDataPaginate
    const filterDataPaginate = useMemo(() => {
        if (pagingInfiniteScroll || pagingOnChangePage) {
            return filterData;
        }

        let currentPage = page - 1;
        if (!uiHidePagination && rowsPerPage != 'ALL') {
            let valuesPaginate = filterData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);
            return valuesPaginate;
        }

        return filterData;
    }, [pagingInfiniteScroll, pagingOnChangePage, filterData, page, uiHidePagination, rowsPerPage]);

    // Key-based stability — recomputes only when visible column set changes
    const visibleColumnsKey = useMemo(() => columns.filter(x => x.show).map(col => col.key || col.field).join(','), [columns]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const visibleColumns = useMemo(() => columns.filter(x => x.show), [visibleColumnsKey, columns]);

    // Calculate column span for empty state row
    const emptyColSpan = useMemo(() => {
        let span = visibleColumns.length;
        if (!selectionDisabled) span += 1; // Checkbox column
        if (menuOptions) span += 1;           // Menu column
        if (customColumn) span += 1;          // Custom column
        return span;
    }, [visibleColumns.length, selectionDisabled, menuOptions, customColumn]);

    // O(1) selected item lookup — replaces O(n) findIndex per row per render
    // Note: For function-based keyColumn, we use the original item's index from selectedItems
    // since the key generation depends on the item's position in the main data array
    const selectedItemsSet = useMemo(() => {
        if (!keyColumn) return new Set();
        if (typeof keyColumn === 'function') {
            // For function keyColumn, store the item references for direct comparison
            return new Set(selectedItems);
        }
        return new Set(selectedItems.map(x => x[keyColumn]));
    }, [selectedItems, keyColumn]);

    // Stable selection toggle callback for memoized row component
    // Selection limit enforcement with deselection always allowed
    const handleToggleSelection = useCallback((row, rowIndex) => {
        setSelectedItems(prev => {
            // For function-based keyColumn, compare by reference since keys depend on index
            const index = typeof keyColumn === 'function'
                ? prev.indexOf(row)
                : keyColumn
                    ? prev.findIndex(x => x[keyColumn] == row[keyColumn])
                    : prev.indexOf(row);
            
            // Deselection - always allowed regardless of limit
            if (index > -1) {
                const next = [...prev];
                next.splice(index, 1);
                onSelectedItemsRef.current && onSelectedItemsRef.current(next);
                return next;
            }
            
            // Selection limit check - callback handles notification
            if (prev.length >= selectionMaxItems) {
                // Call callback instead of NotificationManager
                return prev; // Return unchanged - do not allow selection
            }
            
            // Under limit - allow selection
            const next = [...prev, row];
            onSelectedItemsRef.current && onSelectedItemsRef.current(next);
            return next;
        });
    }, [keyColumn, selectionMaxItems, selectionOnSelectionLimitReached]);


    // Initialize row virtualizer with useVirtualizer
    // useFlushSync: false prevents synchronous rendering on scroll, improving performance
    // Overscan kept at 15 for ~795px scroll buffer to prevent blank rows during fast scroll
    const rowVirtualizer = useVirtualizer({
        // Add skeleton placeholder count when loading more data
        count: filterDataPaginate.length + (pagingInfiniteScroll && isLoadingMore ? virtSkeletonCount : 0),
        getScrollElement: () => tableContainerRef.current,
        // Density-aware row height estimation
        estimateSize: () => {
            if (!uiDefaultDensity) return virtRowHeight;
            return virtBaseRowHeight + Math.max(0, (density - 1) * virtCondensationHeight) * virtHeightMultiplier;
        },
        // Use overscan from virtualizationConfig
        overscan: virtOverscan, // IB-09: Buffer rows — reducing causes blank rows during fast scroll
        useFlushSync: false,
        // measureElement enables dynamic row height measurement
        measureElement: (el) => el?.getBoundingClientRect().height,
        // Stable keys prevent unnecessary re-renders during infinite scroll data appends
        // Supports both string field names and custom key generator functions
        getItemKey: (index) => {
            const item = filterDataPaginate[index];
            if (!item) return index;
            return resolveRowKey(keyColumn, item, index) ?? index;
        },
    });

    // Get virtual items and compute spacer sizes
    const virtualRows = rowVirtualizer.getVirtualItems();
    const totalVirtualSize = rowVirtualizer.getTotalSize();
    const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
    const paddingBottom = virtualRows.length > 0
        ? totalVirtualSize - (virtualRows[virtualRows.length - 1]?.end || 0)
        : 0;

    // Invalidate cached row measurements when density changes
    useEffect(() => { rowVirtualizer.measure(); }, [density]);

    // Track user scroll interaction to prevent premature scroll-fetch on mount (Solution A)
    useEffect(() => {
        const container = tableContainerRef.current;
        if (!container || !pagingInfiniteScroll) return;
        
        const handleScroll = () => {
            // Mark as scrolled when user scrolls down from top
            if (container.scrollTop > 0) {
                hasUserScrolledRef.current = true;
            }
        };
        
        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, [pagingInfiniteScroll]);

    // Reset scroll tracking when data is cleared/refreshed
    useEffect(() => {
        if (filterDataPaginate.length === 0) {
            hasUserScrolledRef.current = false;
        }
    }, [filterDataPaginate.length]);

    // Replace react-waypoint with virtualizer-based scroll detection
    useEffect(() => {
        if (!pagingInfiniteScroll) return;
        
        // Solution A + D: Guard against premature trigger on mount
        // Only trigger infinite scroll after user has actually scrolled
        const container = tableContainerRef.current;
        if (!container || container.scrollTop === 0 || !hasUserScrolledRef.current) return;
        
        const lastItem = virtualRows[virtualRows.length - 1];
        if (!lastItem) return;
        // Use scrollThreshold from virtualizationConfig
        const nearEnd = lastItem.index >= filterDataPaginate.length - virtScrollThreshold;
        if (!nearEnd) return;
        if (loading || isLoadingMore) return;
        // Stop retry loop when there's an error - user must manually retry
        if (error) return;
        // nextToken-based cursor pagination
        if (nextToken) {
            if (pagingOnChangePage) pagingOnChangePage();
            return;
        }

        // page-based offset pagination (legacy)
        if (nextToken === undefined && total) {
            const newPage = page + 1;
            if (filterDataPaginate.length < total) {
                setPage(newPage);
                if (pagingOnChangePage) pagingOnChangePage(newPage, rowsPerPage);
                if (onChangeDisplayData) onChangeDisplayData(filterData);
            }
        }
    }, [virtualRows, pagingInfiniteScroll, filterDataPaginate.length, nextToken, total, page, rowsPerPage, loading, isLoadingMore, pagingOnChangePage, onChangeDisplayData, filterData, error]);

    // Use columns and export.name for CSV export
    const exportCSV = useCallback(() => {
        const headers = columns.filter(x => x.export && x.export)
        let csvContent = `${headers.map(x => x.title).join(',')}` + '\n';


        let rows = filterData;
        if (selectedItems?.length) rows = selectedItems;

        rows.forEach((row, index) => {
            //its necesary format the text because if possible has a line breaks or ,

            let itemValues = headers.map((col) => {
                let value = col.exportValue && col.exportValue(row) || row[col.key] || "";
                value = value.toString().replaceAll(/(\r\n|\n|\r)/gm, "") || ""
                if (value.includes(","))
                    value = `"${value}"`;

                return value
            })


            let dataString = itemValues.join(',');
            csvContent += index < rows.length ? dataString + '\n' : dataString;
        });

        let fileName = `${exportName || 'exported-data'}.csv`;
        let mimeType = 'text/csv;encoding:utf-8;charset=UTF-8';
        let a = document.createElement('a');

        mimeType = mimeType || 'application/octet-stream';

        if (navigator.msSaveBlob) {
            // IE10
            navigator.msSaveBlob(
                new Blob([csvContent], {
                    type: mimeType,
                }),
                fileName,
            );
        } else if (URL && 'download' in a) {
            //html5 A[download]
            a.href = URL.createObjectURL(
                new Blob([csvContent], {
                    type: mimeType,
                }),
            );
            a.setAttribute('download', fileName);
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } else {
            location.href = 'data:application/octet-stream,' + encodeURIComponent(csvContent); // only this mime type is supported
        }
    }, [columns, filterData, selectedItems, exportName]);

    // Use columns and export settings for PDF export
    const exportPDF = useCallback(() => {
        const unit = 'pt';
        const size = 'A4'; // Use A1, A2, A3 or A4
        const orientation = exportOrientation || 'portrait'; // portrait or landscape

        const doc = new jsPDF(orientation, unit, size);
        // Use PDF_FONT_SIZE constant
        doc.setFontSize(PDF_FONT_SIZE);

        jsPDF.autoTableSetDefaults({ headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] } }, doc)

        let rows = filterData;
        if (selectedItems?.length) rows = selectedItems;

        const headers = columns.filter(x => x.show && x.export);
        let content = {
            pageBreak: 'auto',
            // Use PDF_START_Y constant
            startY: PDF_START_Y,
            head: [headers.map(x => x.title)],
            //its necesary loop the data to assign styles for wrap text when is a long value
            body: rows.map((row) => {
                return headers.map((col) => {
                    // Use PDF_MIN_CELL_WIDTH constant
                    return { content: col.exportValue && col.exportValue(row) || row[col.key] || "", styles: { cellWidth: 'auto', overflow: 'linebreak', minCellWidth: PDF_MIN_CELL_WIDTH } }
                })
            }),
        };

        doc.autoTable(content);
        doc.save(`${exportName || 'export'}.pdf`);
    }, [exportOrientation, filterData, selectedItems, columns, exportName]);

    // Callback handler for density changes
    const handleDensityChange = (den) => {
        let value = den ? den : density === DENSITY_MAX ? 1 : density + 1;
        setDensity(value);
        onChangeDensity && onChangeDensity(value);
    }

    // Populate column width cache via useEffect — avoids DOM reads during render
    // Try data cell first, fall back to header cell for initial load
    useEffect(() => {
        if (!lockedColumns) return;
        columns.forEach(col => {
            if (!col.show) return;
            // Try data cell first, fall back to header cell
            const el = document.getElementById(`cell-${col.key}`) ||
                       document.getElementById(`header-${col.key}`);
            if (el) { columnWidthCache.current[col.key] = el.offsetWidth; }
        });
    }, [columns, lockedColumns, filterDataPaginate.length]);

    // Use cached column widths instead of DOM reads in render path
    const lockedColumnsHandler = useCallback((key, visibleColumns, disable, id) => {
        let left = !disable ? virtLockedColumnOffset : 0;
        visibleColumns.filter((x, index) => x?.locked && index < key).forEach((col) => {
            left += columnWidthCache.current[col?.key] || col?.width || 0;
        });
        left = menuOptions ? left + virtLockedColumnOffset : left;
        left = customColumn ? left + virtLockedColumnOffset : left;
        return left;
    }, [menuOptions, customColumn, virtLockedColumnOffset]);

    // Lock vertical scroll position during column drag to prevent scroll interference
    // Lock header cell widths before drag starts to prevent table layout redistribution
    // Only lock NON-draggable cells - draggable cells have width from item.width
    const handleColumnDragStart = useCallback(() => {
        const container = tableContainerRef.current;
        if (!container) return;

        // Lock scroll position
        const savedScrollTop = container.scrollTop;
        const savedScrollLeft = container.scrollLeft;
        const lockScroll = () => {
            container.scrollTop = savedScrollTop;
            container.scrollLeft = savedScrollLeft;
        };
        container.addEventListener('scroll', lockScroll);
        scrollLockRef.current = lockScroll;

        // Lock dimensions ONLY on non-draggable <th> elements
        const headerCells = container.querySelectorAll('th');
        headerCells.forEach((th) => {
            // Skip cells that have a header-* ID (these are draggable column cells)
            if (th.id && th.id.startsWith('header-')) return;
            const rect = th.getBoundingClientRect();
            th.style.width = `${rect.width}px`;
            th.style.minWidth = `${rect.width}px`;
        });

        // Add class for CSS targeting
        container.classList.add('column-dragging');
    }, []);

    const handleColumnDragEnd = useCallback(() => {
        const container = tableContainerRef.current;
        if (!container) return;

        // Unlock scroll
        if (scrollLockRef.current) {
            container.removeEventListener('scroll', scrollLockRef.current);
            scrollLockRef.current = null;
        }

        // Remove locked widths ONLY on non-draggable <th> cells
        const headerCells = container.querySelectorAll('th');
        headerCells.forEach((th) => {
            // Skip cells that have a header-* ID (these are draggable column cells)
            if (th.id && th.id.startsWith('header-')) return;
            th.style.removeProperty('width');
            th.style.removeProperty('min-width');
        });

        // Remove dragging state class
        container.classList.remove('column-dragging');
    }, []);

    /**
     * Retry handler that scrolls to bottom first so user sees loading feedback.
     */
    const handleRetryWithScroll = useCallback(() => {
        // Scroll to bottom first so user sees loading feedback
        const container = tableContainerRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
        // Then trigger the original retry callback
        pagingOnRetry?.();
    }, [pagingOnRetry]);

    // JSX uses resolved configs throughout
    return (
        <div style={{ 
            height: (uiHeight ? uiHeight : (uiTopHeight && `calc(100vh - ${uiTopHeight}px)` || '100%')), 
            minHeight: 500,
            width: '100%',
        }}>
            <Grid item className="table_component">
                <Fragment>
                    <div className="container-loading">
                        {
                            loading && <LinearProgress color="primary" />
                        }
                    </div>
                    {/* REQ-10: Live region for screen reader status announcements */}
                    <div
                        role="status"
                        aria-live="polite"
                        aria-atomic="true"
                        className="sr-only"
                        style={{
                            position: 'absolute',
                            width: '1px',
                            height: '1px',
                            padding: 0,
                            margin: '-1px',
                            overflow: 'hidden',
                            clip: 'rect(0, 0, 0, 0)',
                            whiteSpace: 'nowrap',
                            border: 0
                        }}
                    >
                        {loading && (messages?.ariaLoading || 'Loading data...')}
                        {isLoadingMore && (messages?.ariaLoadingMore || 'Loading more items...')}
                        {error && `${messages?.ariaError || 'Error'}: ${error}`}
                        {!loading && !isLoadingMore && !error && filterDataPaginate.length > 0 && 
                            `${messages?.ariaShowingItems?.replace('{count}', filterDataPaginate.length) || `Showing ${filterDataPaginate.length} items`}`}
                    </div>
                    {/* Attach tableContainerRef to TableContainer for scroll tracking */}
                    {/* CSS containment isolates layout recalculations to this container */}
                    <TableContainer 
                        ref={tableContainerRef} 
                        style={{ contain: 'content', overflowAnchor: 'none', backgroundColor: '#fff' }}
                        role="region"
                        aria-label={messages?.ariaTableRegion || 'Data table'}
                        tabIndex={0}
                    >
                        <Table
                            aria-label={messages?.ariaTableLabel || 'Data table with sortable columns'}
                            aria-rowcount={filterDataPaginate.length}
                            aria-busy={loading || isLoadingMore}
                            aria-multiselectable={!selectionDisabled}
                            size={'medium'}
                            stickyHeader={uiFixHeader}
                        >
                            {/* TableHeader uses resolved configs */}
                            <TableHeader
                                disableItemSelection={selectionDisabled}
                                columnsView={visibleColumns}
                                onChangeColumns={(items) => {
                                    let newColumns = [...columns]
                                    items.forEach((col, index) => {
                                        let indexCol = newColumns.findIndex(x => x.key == col.key)
                                        let newCol = { ...newColumns[indexCol], ...col }
                                        newColumns.splice(indexCol, 1)
                                        newColumns.splice(index, 0, newCol)
                                    })
                                    onChangeColumnsRef.current(newColumns);
                                }
                                }
                                sort={sort}
                                page={page}
                                dataItems={filterDataPaginate}
                                onChangeSort={(newSort) => {
                                    setSort(newSort);
                                    onChangeSort && onChangeSort(newSort);
                                }}
                                selectedItems={selectedItems}
                                onChageSelectedItems={(newSelectedtItems) => {
                                    setSelectedItems(newSelectedtItems)
                                    selectionOnSelectedItems && selectionOnSelectedItems(newSelectedtItems)
                                }}
                                lockedColumns={lockedColumns}
                                lockedColumnsHandler={lockedColumnsHandler}
                                menuOptions={menuOptions}
                                customColumn={customColumn}
                                onColumnDragStart={handleColumnDragStart}
                                onColumnDragEnd={handleColumnDragEnd}
                                // Selection limit props
                                maxSelectableItems={selectionMaxItems}
                                showSelectionCounter={selectionShowCounter}
                                onSelectionLimitReached={selectionOnSelectionLimitReached}
                                // Batch sequential: processed items to skip on Select All
                                processedItemIds={processedItemIds}
                                keyColumn={keyColumn}
                                // i18n: Pass resolved messages for notifications and tooltips
                                messages={messages}
                            />
                            <TableBody>
                                {/* Top padding spacer row for virtualization */}
                                {paddingTop > 0 && (
                                    <tr>
                                        <td colSpan={emptyColSpan} style={{ height: paddingTop, padding: 0, border: 'none' }} />
                                    </tr>
                                )}


                                {/* Render only visible virtual rows instead of all rows */}
                                {/* Also renders skeleton rows during load-more when data[index] is undefined */}
                                {(filterDataPaginate.length > 0 || isLoadingMore) &&
                                    virtualRows.map((virtualRow) => {
                                        const index = virtualRow.index;
                                        const row = filterDataPaginate[index];
                                        
                                        // Render skeleton for placeholder indices (beyond actual data)
                                        if (!row) {
                                            return (
                                                <SkeletonTableRow
                                                    key={`skeleton-${index}`}
                                                    ref={rowVirtualizer.measureElement}
                                                    dataIndex={virtualRow.index}
                                                    visibleColumns={visibleColumns}
                                                    disableItemSelection={selectionDisabled}
                                                    lockedColumns={lockedColumns}
                                                    lockedColumnsHandler={lockedColumnsHandler}
                                                    menuOptions={menuOptions}
                                                    customColumn={customColumn}
                                                    density={density}
                                                    defaultDensity={uiDefaultDensity}
                                                />
                                            );
                                        }
                                        
                                        let printGroup = false
                                        if (grouping) {
                                            let newGroup = row[groupingValueToCompare]
                                            printGroup = !_.isEqual(newGroup, currentGroup)
                                            currentGroup = printGroup ? newGroup : currentGroup
                                        }
                                        // For function keyColumn, check by reference; for string, use the Set lookup
                                        const isSelected = typeof keyColumn === 'function'
                                            ? selectedItemsSet.has(row)
                                            : keyColumn 
                                                ? selectedItemsSet.has(row[keyColumn]) 
                                                : selectedItems.includes(row);
                                        // Resolve row key using helper function for consistent key generation
                                        const rowKey = resolveRowKey(keyColumn, row, index) ?? index;
                                        return (
                                            <Fragment key={rowKey}>
                                                {
                                                    printGroup &&
                                                    <Fragment>
                                                        <TableRow>
                                                            <TableCell colSpan={visibleColumns.length + 1} style={{ padding: 0 }}>
                                                                {
                                                                    groupingArea
                                                                        ? groupingArea(currentGroup, row)
                                                                        : currentGroup.toString()
                                                                }
                                                            </TableCell>
                                                        </TableRow>
                                                    </Fragment>
                                                }

                                                {/* VirtualTableRow uses resolved configs */}
                                                <VirtualTableRow
                                                    ref={rowVirtualizer.measureElement}
                                                    row={row}
                                                    dataIndex={virtualRow.index}
                                                    visibleColumns={visibleColumns}
                                                    disableItemSelection={selectionDisabled}
                                                    lockedColumns={lockedColumns}
                                                    lockedColumnsHandler={lockedColumnsHandler}
                                                    menuOptions={menuOptions}
                                                    onClickMenu={onClickMenu}
                                                    customColumn={customColumn}
                                                    isSelected={isSelected}
                                                    onToggleSelection={handleToggleSelection}
                                                    density={density}
                                                    defaultDensity={uiDefaultDensity}
                                                />
                                            </Fragment>
                                        )
                                    })
                                } 

                                {/* Bottom padding spacer row for virtualization */}
                                {paddingBottom > 0 && (
                                    <tr>
                                        <td colSpan={emptyColSpan} style={{ height: paddingBottom, padding: 0, border: 'none' }} />
                                    </tr>
                                )}

                            </TableBody>
                        </Table>
                        {/* Empty state — rendered outside <Table> with sticky positioning */}
                        {filterDataPaginate.length === 0 && !loading && !isLoadingMore && (
                            <TableEmptyState
                                type={messagesEmptyType || messagesEmptyConfig?.type || 'no-data'}
                                description={messagesEmptyConfig?.description}
                                image={messagesEmptyConfig?.image}
                                actionText={messagesEmptyConfig?.actionText}
                                onAction={messagesEmptyConfig?.onAction}
                                messages={{
                                    initial: messages?.initial,
                                    noData: messages?.noData,
                                    filteredEmpty: messages?.filteredEmpty,
                                    searchEmpty: messages?.searchEmpty,
                                    error: messages?.error
                                }}
                            />
                        )}
                        {/* Indicators outside <Table> but inside <TableContainer> for sticky positioning */}
                        {pagingInfiniteScroll && isLoadingMore && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: `${LOADING_INDICATOR_PADDING}px 0`, position: 'sticky', left: 0 }}>
                                <CircularProgress size={LOADING_SPINNER_SIZE} />
                            </div>
                        )}
                        {/* Only show 'No more items' when data exists AND no more rows available */}
                        {pagingInfiniteScroll && filterDataPaginate.length > 0 && !moreRowsAvailable && !isLoadingMore && !error && (
                            <div className="no-more-items" style={{ position: 'sticky', left: 0 }}>
                                {messagesNoMoreItemsMessage || 'There are no more items'}
                            </div>
                        )}
                        {/* Error indicator for failed load-more operations */}
                        {pagingInfiniteScroll && error && !loading && !isLoadingMore && filterDataPaginate.length > 0 && (
                            <ErrorIndicator
                                message={messagesErrorLoadingMore || error}
                                onRetry={handleRetryWithScroll}
                                retryLabel={messagesRetry}
                            />
                        )}
                    </TableContainer>
                </Fragment>
            </Grid>

            {/* Use resolved configs for pagination */}
            {
                (!uiHidePagination) &&
                <Grid item xs>
                    <divs
                        style={{
                            // Use PAGINATION_PADDING_TOP constant
                            padding: `${PAGINATION_PADDING_TOP}px 0px 0px 0px`
                        }}
                    >
                        <Pagination
                            page={page}
                            rowsPerPage={rowsPerPage}
                            count={total}
                            pagination
                            hideAllInPagination={uiHideAllInPagination}
                            onChangePage={(event, value) => {
                                setPage(value);
                                if (pagingOnChangePage) pagingOnChangePage(value);
                                if (onChangeDisplayData) onChangeDisplayData(filterData);
                                setSelectedItems([]);
                                selectionOnSelectedItems && selectionOnSelectedItems([])
                            }}
                            onChangeRowsPerPage={(value) => {
                                setRowsPerPage(value);
                                setPage(1);
                                if (pagingOnChangeRowsPerPage) pagingOnChangeRowsPerPage(value);
                                if (onChangeDisplayData) onChangeDisplayData(filterData);
                                setSelectedItems([]);
                                selectionOnSelectedItems && selectionOnSelectedItems([])
                            }}
                            valuesPerPage={pagingValuesPerPage}
                        />
                    </divs>
                </Grid>
            }
        </div>
    )
})

export default TableView

const getClearValue = (value) => {
    if (!value) return ""

    if (typeof value == 'undefined') return ""

    return value
}

const multipleSort = (data, sorts) => {

    return [...data].sort((a, b) => {
        let i = 0, result = 0;
        while (i < sorts.length && result === 0) {
            result = (sorts[i].order == 'asc' ? 1 : -1) * (getClearValue(a[sorts[i].key]).toString() < getClearValue(b[sorts[i].key]).toString() ? -1 : (getClearValue(a[sorts[i].key]).toString() > getClearValue(b[sorts[i].key]).toString() ? 1 : 0));
            i++;
        }
        return result;
    })

}
